import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { FollowUp } from '@neuroedge/shared';
import { Button, Card, PageState } from '../../components/ui';
import {
  CalendarView,
  dayBounds,
  dayKey,
  startOfMonth,
  visibleRange,
  type CalendarEvent,
} from '../../components/calendar/CalendarView';
import { useFollowUps } from '../../api/follow-ups';
import { useAuth } from '../../auth/AuthContext';
import { AddFollowUpQuickModal } from '../dashboard/AddFollowUpQuickModal';
import { FollowUpList } from './FollowUpList';

function tone(f: FollowUp): CalendarEvent['tone'] {
  if (f.status === 'COMPLETED') return 'success';
  if (f.status === 'CANCELLED' || f.status === 'MISSED') return 'muted';
  return new Date(f.dueAt).getTime() < Date.now() ? 'danger' : 'default';
}

function eventLabel(f: FollowUp): string {
  const time = new Date(f.dueAt).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
  const what = f.title ?? f.contact?.displayName ?? f.lead?.title ?? 'Follow-up';
  return `${time} ${what}`;
}

/**
 * Follow-ups as a month calendar: each day shows how many follow-ups fall
 * on it (overdue in red, done in green); picking a day lists that day's
 * follow-ups below with their actions (complete, reschedule, reassign) and
 * a quick-add. "Mine" / "Everyone" switches whose follow-ups are shown,
 * and overdue ones are one click away.
 */
export function FollowUpsPage() {
  const [mine, setMine] = useState(true);
  const [month, setMonth] = useState(() => startOfMonth(new Date()));
  const [selected, setSelected] = useState(() => new Date());
  const [showOverdue, setShowOverdue] = useState(false);
  const [adding, setAdding] = useState(false);
  const canManage = useAuth().hasPermission('tasks.manage');

  const range = visibleRange(month);
  const followUps = useFollowUps({
    mine: mine || undefined,
    dueAfter: range.from.toISOString(),
    dueBefore: range.to.toISOString(),
    sortBy: 'dueAt',
    sortOrder: 'asc',
    pageSize: 1000,
  });
  const overdue = useFollowUps({ mine: mine || undefined, overdue: true, pageSize: 1 });
  const overdueCount = overdue.data?.meta.total ?? 0;

  const events: CalendarEvent[] = (followUps.data?.items ?? []).map((f) => ({
    id: f.id,
    start: f.dueAt,
    label: eventLabel(f),
    tone: tone(f),
  }));

  const day = dayBounds(selected);
  const dayLabel = selected.toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}
      >
        <h1 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>Follow-ups</h1>
        <span style={{ flex: 1 }} />
        <Button
          variant={mine ? 'primary' : 'secondary'}
          aria-pressed={mine}
          onClick={() => setMine(true)}
        >
          Mine
        </Button>
        <Button
          variant={!mine ? 'primary' : 'secondary'}
          aria-pressed={!mine}
          onClick={() => setMine(false)}
        >
          Everyone
        </Button>
        <Button
          variant={showOverdue ? 'primary' : 'secondary'}
          aria-pressed={showOverdue}
          onClick={() => setShowOverdue((v) => !v)}
        >
          Overdue{overdue.data ? ` (${overdueCount})` : ''}
        </Button>
        {canManage ? (
          <Button type="button" onClick={() => setAdding(true)}>
            + New follow-up
          </Button>
        ) : null}
      </div>
      {adding ? (
        <AddFollowUpQuickModal initialDay={selected} onClose={() => setAdding(false)} />
      ) : null}

      {showOverdue ? (
        <FollowUpList
          title={mine ? 'Overdue - assigned to me' : 'Overdue - everyone'}
          query={{ mine: mine || undefined, overdue: true }}
          showTargets
        />
      ) : null}

      <Card>
        {followUps.isError ? (
          <PageState
            kind="error"
            message={followUps.error.message}
            onRetry={() => void followUps.refetch()}
          />
        ) : (
          <CalendarView
            month={month}
            onMonthChange={setMonth}
            events={events}
            selectedDay={dayKey(selected)}
            onSelectDay={(d) => {
              setSelected(d);
              if (d.getMonth() !== month.getMonth()) setMonth(startOfMonth(d));
            }}
            loading={followUps.isFetching}
            itemNoun="follow-up"
          />
        )}
      </Card>

      <FollowUpList
        title={`${dayLabel}${mine ? ' - assigned to me' : ''}`}
        query={{
          mine: mine || undefined,
          dueAfter: day.from.toISOString(),
          dueBefore: day.to.toISOString(),
        }}
        showTargets
      />

      <p style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
        <Link to="/">← Back to dashboard</Link>
      </p>
    </div>
  );
}
